import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MassageIcon, CirculationIcon, FaceMassageIcon, SteamBathIcon, ZenRitualIcon, GoldenGlowIcon, JadeBedIcon, BodyPeelingIcon } from './SpaIcons';
interface TreatmentCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  pricing: React.ReactNode;
  delay: number;
  inView: boolean;
  accent?: string;
}
function TreatmentCard({
  icon,
  title,
  description,
  pricing,
  delay,
  inView,
  accent = 'terracotta'
}: TreatmentCardProps) {
  const accentBg = accent === 'sage' ? 'bg-sage/8' : 'bg-terracotta/8';
  const accentBorder = accent === 'sage' ? 'bg-sage/40' : 'bg-terracotta/40';
  return <motion.article initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay
  }} className="group relative bg-cream rounded-2xl p-8 md:p-10 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden" data-id="element-357">
      <div className={`absolute top-0 left-8 right-8 h-[2px] ${accentBorder} rounded-full`} data-id="element-358" />
      <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full ${accentBg} opacity-50 blur-2xl pointer-events-none`} data-id="element-359" />

      <div className="relative" data-id="element-360">
        <div className={`w-16 h-16 rounded-2xl ${accentBg} flex items-center justify-center mb-6`} data-id="element-361">
          {icon}
        </div>
        <h3 className="font-heading text-2xl font-semibold text-bark mb-3" data-id="element-362">
          {title}
        </h3>
        <p className="font-body text-bark-light leading-relaxed mb-6" data-id="element-363">
          {description}
        </p>
        <div className="w-10 h-px bg-stone mb-4" data-id="element-364" />
        {pricing}
      </div>
    </motion.article>;
}
export function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const ritualsRef = useRef(null);
  const ritualsInView = useInView(ritualsRef, {
    once: true,
    margin: '-80px'
  });
  const spaRef = useRef(null);
  const spaInView = useInView(spaRef, {
    once: true,
    margin: '-80px'
  });
  return <section id="services" className="w-full bg-warm-white py-24 md:py-32 px-6 lg:px-8" data-id="element-365">
      <div className="max-w-6xl mx-auto" ref={ref} data-id="element-366">
        {/* ═══════════════════════════════════════════
            SECTION HEADER
            ═══════════════════════════════════════════ */}
        <motion.div className="text-center mb-16 md:mb-20" initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-367">
          <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage mb-4" data-id="element-368">
            Treatments & Rituals
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark mb-6" data-id="element-369">
            Our Rituals
          </h2>
          <p className="font-body text-bark-light text-base md:text-lg max-w-xl mx-auto leading-relaxed" data-id="element-370">
            Every treatment is designed to help you feel relaxed, balanced, and
            renewed.
          </p>
        </motion.div>

        {/* ═══════════════════════════════════════════
            1. MASSAGES & THERAPIES
            ═══════════════════════════════════════════ */}
        <div className="mb-6 md:mb-8" data-id="element-371">
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5
        }} className="text-xs font-body font-medium tracking-[0.2em] uppercase text-bark-light/50 mb-6" data-id="element-372">
            Massages & Therapies
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8" data-id="element-373">
          <TreatmentCard icon={<MassageIcon className="w-9 h-9 text-terracotta" data-id="element-375" />} title="Customised Massage" description="A soothing aromatic massage made just for you. We adapt every session to what your body needs, helping you feel calm and refreshed." delay={0.1} inView={inView} pricing={<div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-body font-medium text-terracotta" data-id="element-376">
                <span data-id="element-377">30 min — 150 RON</span>
                <span className="text-stone" data-id="element-378">·</span>
                <span data-id="element-379">60 min — 250 RON</span>
                <span className="text-stone" data-id="element-380">·</span>
                <span data-id="element-381">90 min — 350 RON</span>
              </div>} data-id="element-374" />

          <TreatmentCard icon={<CirculationIcon className="w-9 h-9 text-terracotta" data-id="element-383" />} title="Pressotherapy" description="A gentle treatment that improves blood flow and helps reduce swelling, water retention, and muscle tension." delay={0.2} inView={inView} pricing={<p className="text-sm font-body font-medium text-terracotta" data-id="element-384">
                30 min — 100 RON
              </p>} data-id="element-382" />

          <TreatmentCard icon={<FaceMassageIcon className="w-9 h-9 text-terracotta" data-id="element-386" />} title="Pressotherapy with Face & Head Massage" description="Combine pressotherapy with a relaxing face and head massage for a complete feel-good experience." delay={0.3} inView={inView} pricing={<p className="text-sm font-body font-medium text-terracotta" data-id="element-387">
                30 min — 180 RON
              </p>} data-id="element-385" />

          <TreatmentCard icon={<BodyPeelingIcon className="w-9 h-9 text-terracotta" data-id="element-389" />} title="Body Peeling Delight" description="A revitalizing body scrub using natural salt and essential oils. This gentle exfoliation removes dead skin cells, boosts circulation, and leaves your skin feeling silky smooth and deeply nourished." delay={0.4} inView={inView} pricing={<p className="text-sm font-body font-medium text-terracotta" data-id="element-390">
                30 min — 100 RON
              </p>} data-id="element-388" />

          <TreatmentCard icon={<JadeBedIcon className="w-9 h-9 text-terracotta" data-id="element-392" />} title="Jade & Tourmaline Massage Bed" description="A deeply restorative therapy blending gentle acupressure with soothing infrared heat. Warm jade rollers glide along the body, releasing tension and improving circulation, while tourmaline crystals support detoxification and energetic balance." delay={0.4} inView={inView} pricing={<p className="text-sm font-body font-medium text-terracotta" data-id="element-393">
                40 min — 100 RON
              </p>} data-id="element-391" />
        </div>
      </div>

      {/* ═══════════════════════════════════════════
                2. SIGNATURE RITUALS
                ═══════════════════════════════════════════ */}
      <div className="max-w-6xl mx-auto my-20 md:my-28 flex items-center gap-6" ref={ritualsRef} data-id="element-394">
        <div className="flex-1 h-px bg-stone" data-id="element-395" />
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={ritualsInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage whitespace-nowrap" data-id="element-396">
          Signature Rituals
        </motion.p>
        <div className="flex-1 h-px bg-stone" data-id="element-397" />
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10" data-id="element-398">
        {/* "Zen Barn" Ritual */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} animate={ritualsInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="relative bg-cream rounded-2xl p-10 md:p-12 overflow-hidden" data-id="element-399">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sage/0 via-sage/40 to-sage/0" data-id="element-400" />
          <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-sage/5 blur-3xl pointer-events-none" data-id="element-401" />
          <div className="absolute top-8 right-8 opacity-[0.07]" data-id="element-402">
            <ZenRitualIcon className="w-32 h-32 text-sage" data-id="element-403" />
          </div>

          <div className="relative" data-id="element-404">
            <div className="flex items-center gap-3 mb-8" data-id="element-405">
              <div className="w-14 h-14 rounded-2xl bg-sage/10 flex items-center justify-center" data-id="element-406">
                <ZenRitualIcon className="w-8 h-8 text-sage" data-id="element-407" />
              </div>
              <span className="text-xs font-body font-medium tracking-[0.2em] uppercase text-sage/70 px-3 py-1 rounded-full border border-sage/20" data-id="element-408">
                Signature
              </span>
            </div>

            <h3 className="font-heading text-3xl md:text-4xl font-semibold text-bark leading-tight mb-5" data-id="element-409">
              "Zen Barn" Ritual
            </h3>
            <p className="font-body text-bark-light text-base md:text-lg leading-relaxed mb-4" data-id="element-410">
              Our signature experience. Includes a gentle body scrub with
              natural products, a full body massage, and a soothing facial
              treatment to leave you feeling completely renewed.
            </p>
            <p className="font-body text-bark-light/70 text-base leading-relaxed mb-8" data-id="element-411">
              The ultimate retreat within a retreat — two hours of uninterrupted
              care, designed to restore your body and quiet your mind.
            </p>

            <div className="w-12 h-px bg-sage/30 mb-5" data-id="element-412" />

            <div className="flex items-baseline justify-between" data-id="element-413">
              <p className="font-heading text-2xl font-semibold text-bark" data-id="element-414">
                <span className="text-terracotta" data-id="element-415">500 RON</span>
              </p>
              <p className="font-body text-sm text-bark-light" data-id="element-416">120 min</p>
            </div>
          </div>
        </motion.article>

        {/* "Golden Body Glow" Ritual */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} animate={ritualsInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.25
      }} className="relative bg-cream rounded-2xl p-10 md:p-12 overflow-hidden" data-id="element-417">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-terracotta/0 via-terracotta/40 to-terracotta/0" data-id="element-418" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-terracotta/5 blur-3xl pointer-events-none" data-id="element-419" />
          <div className="absolute top-8 right-8 opacity-[0.07]" data-id="element-420">
            <GoldenGlowIcon className="w-32 h-32 text-terracotta" data-id="element-421" />
          </div>

          <div className="relative" data-id="element-422">
            <div className="flex items-center gap-3 mb-8" data-id="element-423">
              <div className="w-14 h-14 rounded-2xl bg-terracotta/10 flex items-center justify-center" data-id="element-424">
                <GoldenGlowIcon className="w-8 h-8 text-terracotta" data-id="element-425" />
              </div>
              <span className="text-xs font-body font-medium tracking-[0.2em] uppercase text-terracotta/70 px-3 py-1 rounded-full border border-terracotta/20" data-id="element-426">
                Signature
              </span>
            </div>

            <h3 className="font-heading text-3xl md:text-4xl font-semibold text-bark leading-tight mb-5" data-id="element-427">
              "Golden Body Glow" Ritual
            </h3>
            <p className="font-body text-bark-light text-base md:text-lg leading-relaxed mb-4" data-id="element-428">
              A special scrub and massage using crystals and unique oils. This
              ancient-inspired ritual brings back your natural glow and leaves
              your skin feeling soft and radiant.
            </p>
            <p className="font-body text-bark-light/70 text-base leading-relaxed mb-8" data-id="element-429">
              Inspired by centuries-old beauty traditions, this ritual uses the
              natural warmth of crystals and hand-selected botanical oils to
              nourish, smooth, and illuminate your skin.
            </p>

            <div className="w-12 h-px bg-terracotta/30 mb-5" data-id="element-430" />

            <div className="flex items-baseline justify-between" data-id="element-431">
              <p className="font-heading text-2xl font-semibold text-bark" data-id="element-432">
                <span className="text-terracotta" data-id="element-433">700 RON</span>
              </p>
              <p className="font-body text-sm text-bark-light" data-id="element-434">120 min</p>
            </div>
          </div>
        </motion.article>
      </div>

      {/* ═══════════════════════════════════════════
                3. SPA EXPERIENCES
                ═══════════════════════════════════════════ */}
      <div className="max-w-6xl mx-auto my-20 md:my-28 flex items-center gap-6" ref={spaRef} data-id="element-435">
        <div className="flex-1 h-px bg-stone" data-id="element-436" />
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={spaInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage whitespace-nowrap" data-id="element-437">
          Spa Experiences
        </motion.p>
        <div className="flex-1 h-px bg-stone" data-id="element-438" />
      </div>

      <div className="max-w-6xl mx-auto pb-8" data-id="element-439">
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} animate={spaInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="group relative bg-cream rounded-2xl p-8 md:p-10 lg:p-12 overflow-hidden" data-id="element-440">
          <div className="absolute top-0 left-8 right-8 h-[2px] bg-sage/40 rounded-full" data-id="element-441" />
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-sage/8 opacity-50 blur-2xl pointer-events-none" data-id="element-442" />

          <div className="relative grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-center" data-id="element-443">
            <div className="w-16 h-16 rounded-2xl bg-sage/10 flex items-center justify-center" data-id="element-444">
              <SteamBathIcon className="w-9 h-9 text-sage" data-id="element-445" />
            </div>
            <div data-id="element-446">
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-bark mb-3" data-id="element-447">
                Sauna, Jacuzzi & Hot Tub
              </h3>
              <p className="font-body text-bark-light leading-relaxed mb-4 max-w-2xl" data-id="element-448">
                Enjoy our outdoor Finnish sauna, jacuzzi, and hot tub surrounded
                by the peaceful Transylvanian hills.
              </p>
              <div className="flex items-baseline gap-4" data-id="element-449">
                <p className="text-sm font-body font-medium text-terracotta" data-id="element-450">
                  90 min — 150 RON / person
                </p>
                <p className="text-xs font-body text-bark-light" data-id="element-451">
                  *Minimum 2 people
                </p>
              </div>
            </div>
          </div>
        </motion.article>
      </div>
    </section>;
}